import React, { useEffect, useMemo, useState } from 'react';
import { View, StyleProp, TextStyle } from 'react-native';
import { translate } from '../services/translate';
import MyAppText from './MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FieldValues, RegisterOptions, useController, UseFormReturn } from 'react-hook-form';
import { DropDownPickerProps, ItemType, ValueType } from 'react-native-dropdown-picker';
import { DistributiveOmit } from '@tanstack/react-query';
import MyDropDownPicker from './MyDropDownPicker';
import { ScrollView } from 'react-native-web-hover';
import Badge from './Badge';
import getBestContrastColor from '../services/best-contrast-color';
import { CustomTooltip } from './CustomTooltip';

type CustomType = ItemType<ValueType> & {
    color?: string;
    textColor?: string;
};

type FormDropDownPicker = {
    label?: string;
    required?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    viewStyle?: StyleProp<TextStyle>;
    labelFontSize?: number;
    labelStyle?: StyleProp<TextStyle>;
    semiBoldLabel?: boolean;
    showTooltip?: boolean;
    tooltipText?: string;
    isLoading?: boolean;
    externalSelect?: boolean;
    selectedLabel?: string;
    selectedLabelFontSize?: number;
    selectedLabelStyle?: StyleProp<TextStyle>;
    onRemove?: (items: ValueType[]) => void;
    horizontalScroll?: boolean;
    scrollMaxHeight?: number;
    badgeTextColor?: string;
    readOnly?: boolean;
    searchable?: boolean;
};

export default function FormDropDownPicker(props: DistributiveOmit<DropDownPickerProps<ValueType>, 'open' | 'setOpen' | 'value' | 'setValue'> & FormDropDownPicker) {
    const { styles, theme } = useStyles(stylesheet);

    const [isOpen, setIsOpen] = useState(false);
    const [multiValue, setMultiValue] = useState(props.form.getValues(props.fieldName) || null);
    const [didItAlreadyOpen, setDidItAlreadyOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState<CustomType[]>([]);

    const rules = useMemo(() => {
        const r: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        if (props.required) {
            r.required = translate('required_field');
            r.validate = {
                valueMustBeInsideItems: v => {
                    if (Array.isArray(v)) {
                        if (v.length === 0) {
                            return translate('required_field');
                        }
                        return v.every(item => props.items.map(i => i.value).includes(item)) || translate('required_field');
                    } else {
                        return props.items.map(i => i.value).includes(v) || translate('required_field');
                    }
                }
            };
        }

        return r;
    }, [props.required, props.items]);

    const {
        field: { onChange },
        fieldState
    } = useController({ name: props.fieldName, control: props.form.control, rules });

    useEffect(() => {
        props.form.register(props.fieldName, props.required ? {
            required: translate('required_field'),
            validate: {
                valueMustBeInsideItems: v => {
                    if (Array.isArray(v)) {
                        if (v.length === 0) {
                            return translate('required_field');
                        }
                        return v.every(item => props.items.map(i => i.value).includes(item)) || translate('required_field');
                    } else {
                        return props.items.map(i => i.value).includes(v) || translate('required_field');
                    }
                }
            }
        } : {
            required: false,
            validate: {}
        });

        props.form.trigger(props.fieldName);
    }, [props.required]);


    useEffect(() => {
        if (props.externalSelect && multiValue) {
            const items = Array.isArray(multiValue) ? multiValue : [multiValue];
            const selected = items.map(value => {
                const item = props.items.find(i => i.value === value) as CustomType | undefined;
                return {
                    value: item?.value ?? value,
                    label: item?.label ?? '',
                    color: item?.color ?? '',
                    textColor: item?.textColor ?? undefined
                };
            }).filter(i => i.label !== '');
            setSelectedItems(selected);
        }
    }, [multiValue, props.externalSelect, props.items]);

    useEffect(() => {
        onChange(multiValue);
    }, [multiValue]);

    useEffect(() => {
        setMultiValue(props.form.getValues(props.fieldName));
    }, [props.form.watch(props.fieldName)]);

    useEffect(() => {
        props.form.trigger(props.fieldName);
    }, [props.items]);

    useEffect(() => {
        if (!isOpen && didItAlreadyOpen) {
            props.form.setValue(props.fieldName, props.form.getValues(props.fieldName), { shouldDirty: true, shouldTouch: true });
            setDidItAlreadyOpen(false);
        }
        if (isOpen) {
            setDidItAlreadyOpen(true);
        }
    }, [isOpen, didItAlreadyOpen]);

    function onSelectItem(item: CustomType | CustomType[]) {
        if (!Array.isArray(item)) {
            const currentValues = Array.isArray(multiValue) ? multiValue : (multiValue ? [multiValue] : []);
            const index = currentValues.findIndex(v => v === item.value);

            if (index !== -1) {
                const newValues = [...currentValues];
                newValues.splice(index, 1);
                setMultiValue(newValues.length > 0 ? newValues : null);
            } else {
                if (props.multiple) {
                    setMultiValue([...currentValues, item.value]);
                } else {
                    setMultiValue(item.value);
                }
            }
        } else {
            setMultiValue(item.map(i => i.value));
        }
    }

    function removeItem(index: number) {
        const newItems = [...selectedItems];
        newItems.splice(index, 1);
        setSelectedItems(newItems);

        const newValues = newItems.map(i => i.value ?? 0);
        setMultiValue(newValues.length > 0 ? newValues : null);

        if (props.onRemove) {
            props.onRemove(newValues);
        }
    }

    return (
        <View style={[styles.inputContainer(props.zIndex), props.viewStyle]}>
            {props.label ?
                <View style={{ flexDirection: 'row', gap: 5, zIndex: (props.zIndex || 0) + 1 }}>
                    <MyAppText
                        style={props.labelStyle ? props.labelStyle : styles.filterText(props.labelFontSize ?? 16, props.semiBoldLabel)}>
                        {props.label}
                    </MyAppText>
                    {props.required ?
                        <MyAppText style={styles.filterTextRequired(props.semiBoldLabel)}>(*)</MyAppText>
                        : null}
                    {props.showTooltip && props.tooltipText && <CustomTooltip
                        content={props.tooltipText}
                        tooltipDirection={'right'}
                    />}
                </View>
                : null}
            <MyDropDownPicker
                {...props}
                labelStyle={undefined}
                borderColor={theme.colors.field.border}
                searchable={props.searchable != undefined ? props.searchable : true}
                height={40}
                open={isOpen}
                value={multiValue}
                setValue={(val) => {
                    if (!props.externalSelect) {
                        setMultiValue(val);
                    }
                }}
                setOpen={setIsOpen}
                {...(props.externalSelect && {
                    mode: 'SIMPLE',
                    labelStyle: { color: 'transparent' },
                    onSelectItem: onSelectItem
                })}
            />

            {props.externalSelect && (
                <View style={{ marginTop: 8, gap: 8 }}>
                    {props.selectedLabel ? (
                        <MyAppText style={props.selectedLabelStyle ? props.selectedLabelStyle : styles.selectedLabel(props.selectedLabelFontSize ?? 14)}>
                            {props.selectedLabel}
                        </MyAppText>
                    ) : null}
                    <ScrollView
                        horizontal={props.horizontalScroll}
                        style={{ maxHeight: props.scrollMaxHeight ?? 80 }}
                        contentContainerStyle={{ flexDirection: 'row', columnGap: 8, rowGap: 4, flexWrap: 'wrap' }}
                    >
                        {selectedItems.map((item, index) => (
                            <Badge
                                key={index}
                                backgroundColor={item.color}
                                mode='ROUNDED'
                                textColor={
                                    props.badgeTextColor
                                        ? props.badgeTextColor
                                        : item.textColor
                                            ? item.textColor
                                            : getBestContrastColor(item.color ?? '')
                                }
                                removable={!props.readOnly}
                                onRemove={() => removeItem(index)}
                            >
                                {item.label}
                            </Badge>
                        ))}
                    </ScrollView>
                </View>
            )}

            {fieldState.error && fieldState.isTouched ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
        </View>
    );

}

const stylesheet = createStyleSheet(theme => ({
    inputContainer: (zIndex: number | undefined) => ({
        zIndex,
        minWidth: 200,
    }),
    filterText: (fontSize: number, semiBoldLabel?: boolean) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
        fontWeight: semiBoldLabel ? '600' : '400'
    }),
    filterTextRequired: (semiBoldLabel?: boolean) => ({
        color: theme.colors.errors['300'],
        fontWeight: semiBoldLabel ? '600' : '400'
    }),
    selectedLabel: (fontSize: number) => ({
        fontSize,
        color: theme.colors.primary['500'],
        fontWeight: '600',
        textTransform: 'uppercase',
    }),
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10
    },
}));
